import React, { useCallback, useEffect, useMemo, useState } from "react";
import { History, RotateCcw, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../../components/layout/AdminShell";
import EmptyState from "../../components/EmptyState";
import { useI18n } from "../../i18n";
import { AuditApi, errorMessage } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const PAGE = 50;

const TONE = {
  receipt: "bg-primary/10 text-primary",
  tenant: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
  user: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  report: "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-300",
};

const EMPTY_FILTERS = { action: "all", actor_id: "all", date_from: "", date_to: "", q: "" };

const metaSummary = (meta) => {
  if (!meta || typeof meta !== "object") return "";
  const parts = Object.entries(meta)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => {
      const value = Array.isArray(v)
        ? v.slice(0, 3).join(", ")
        : typeof v === "object"
          ? JSON.stringify(v)
          : String(v);
      return `${k.replace(/_/g, " ")}: ${value}`;
    });
  return parts.join(" · ");
};

const AuditLog = () => {
  const { t } = useI18n();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [options, setOptions] = useState({ actions: [], actors: [] });
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);

  useEffect(() => {
    AuditApi.filters()
      .then(setOptions)
      .catch(() => setOptions({ actions: [], actors: [] }));
  }, []);

  const query = useMemo(() => {
    const p = { limit: PAGE, skip: 0 };
    if (filters.action !== "all") p.action = filters.action;
    if (filters.actor_id !== "all") p.actor_id = filters.actor_id;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.q.trim()) p.q = filters.q.trim();
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AuditApi.list(query);
      setEvents(res.events || []);
      setTotal(res.total || 0);
    } catch (e) {
      toast.error(errorMessage(e, "Could not load the audit log"));
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const loadMore = async () => {
    setMore(true);
    try {
      const res = await AuditApi.list({ ...query, skip: events.length });
      setEvents((list) => [...list, ...(res.events || [])]);
    } catch (e) {
      toast.error(errorMessage(e, "Could not load more events"));
    } finally {
      setMore(false);
    }
  };

  const actionLabel = (action) => {
    const key = String(action || "").replace(/\./g, "_");
    const label = t(`audit.actions.${key}`);
    return label.startsWith("audit.actions.") ? String(action).replace(/[._]/g, " ") : label;
  };

  const dirty = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  return (
    <AdminShell title={t("audit.title")} subtitle={t("audit.sub")}>
      <div className="ll-card p-4" data-testid="audit-filters">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("audit.action")}</Label>
            <Select
              value={filters.action}
              onValueChange={(v) => setFilters((f) => ({ ...f, action: v }))}
            >
              <SelectTrigger data-testid="audit-filter-action">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.all")}</SelectItem>
                {(options.actions || []).map((a) => (
                  <SelectItem key={a} value={a}>
                    {actionLabel(a)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">{t("audit.actor")}</Label>
            <Select
              value={filters.actor_id}
              onValueChange={(v) => setFilters((f) => ({ ...f, actor_id: v }))}
            >
              <SelectTrigger data-testid="audit-filter-actor">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.all")}</SelectItem>
                {(options.actors || []).map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="audit-from">
              {t("common.from")}
            </Label>
            <Input
              id="audit-from"
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
              data-testid="audit-filter-date-from"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="audit-to">
              {t("common.to")}
            </Label>
            <Input
              id="audit-to"
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
              data-testid="audit-filter-date-to"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="audit-q">
              {t("common.search")}
            </Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="audit-q"
                className="pl-8"
                placeholder={t("audit.searchPlaceholder")}
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                data-testid="audit-filter-search"
              />
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground" data-testid="audit-total">
            {t("audit.count", { shown: events.length, total })}
          </p>
          {dirty ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => setFilters(EMPTY_FILTERS)}
              data-testid="audit-reset-filters"
            >
              <RotateCcw className="h-3.5 w-3.5" /> {t("common.reset")}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="ll-card mt-4 overflow-hidden">
        {loading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 rounded-lg" />
            ))}
          </div>
        ) : events.length ? (
          <>
            <Table data-testid="audit-table">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead className="w-[170px]">{t("audit.when")}</TableHead>
                  <TableHead className="w-[180px]">{t("audit.actor")}</TableHead>
                  <TableHead>{t("audit.action")}</TableHead>
                  <TableHead className="w-[120px]">{t("audit.entity")}</TableHead>
                  <TableHead>{t("audit.details")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((e) => (
                  <TableRow key={e.id} data-testid={`audit-row-${e.id}`}>
                    <TableCell className="ll-num whitespace-nowrap text-xs text-muted-foreground">
                      {formatDateTime(e.created_at)}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      <span className="flex items-center gap-1.5">
                        {e.actor_id === "system" ? (
                          <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : null}
                        <span className="truncate">{e.actor_name}</span>
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{actionLabel(e.action)}</TableCell>
                    <TableCell>
                      {e.entity ? (
                        <Badge
                          variant="secondary"
                          className={TONE[e.entity] || ""}
                          title={e.entity_id || ""}
                        >
                          {t(`audit.entities.${e.entity}`).startsWith("audit.entities.")
                            ? e.entity
                            : t(`audit.entities.${e.entity}`)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell
                      className="max-w-[420px] truncate text-xs text-muted-foreground"
                      title={metaSummary(e.meta)}
                    >
                      {metaSummary(e.meta) || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {events.length < total ? (
              <div className="border-t p-3 text-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  disabled={more}
                  data-testid="audit-load-more"
                >
                  {more ? t("common.loading") : t("audit.loadMore")}
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={History}
              title={t("audit.emptyTitle")}
              description={dirty ? t("audit.emptyFiltered") : t("audit.emptyBody")}
              testId="audit-empty"
            />
          </div>
        )}
      </div>
    </AdminShell>
  );
};

export default AuditLog;
